<template lang="pug">
.div
  // Chip de ubicación
  v-chip(
    v-if="showChip"
    @click="showLocationDialog = true"
    class="location-chip"
    :loading="gettingLocation || cityLoading"
    :color="chipColor"
    label
    outlined
  )
    address-icon(v-if="showIcon", :color="chipTextColor")
    span.location-text(:class="textColorClass") {{ currentCity || 'Elegir ubicación' }}
    v-icon(right v-if="showIcon" :color="chipTextColor") mdi-chevron-down

  // Diálogo de ubicación
  v-dialog(
    v-model="showLocationDialog"
    max-width="500"
    persistent
  )
    v-card
      v-card-title.subtitle-1
        | Seleccionar ubicación
        v-spacer
        v-btn(icon @click="closeLocationDialog")
          v-icon mdi-close
      v-card-text
        v-autocomplete(
          v-model="selectedPlaceId"
          :items="citySuggestions"
          item-text="description"
          item-value="place_id"
          :search-input.sync="locationSearch"
          :loading="cityLoading || gettingLocation"
          :error="!!locationError"
          :error-messages="locationError"
          label="Buscar ciudad"
          clearable
          outlined
          dense
          hide-no-data
          :filter="accentInsensitiveFilter"
          @update:search-input="onCityInput"
          @change="onCitySelected"
        )
        v-btn.mt-2(
          block
          text
          color="primary"
          @click="getCurrentLocation"
          :loading="gettingLocation"
        )
          v-icon(left) mdi-crosshairs-gps
          | Usar mi ubicación actual
      v-card-actions
        v-spacer
        v-btn(text @click="closeLocationDialog") Cancelar
        v-btn(color="primary" @click="confirmLocation") Confirmar
</template>

<script>
import { mapState, mapActions } from 'vuex'
import AddressIcon from '@/assets/icons/AddressIcon.vue'
import debounce from 'lodash/debounce'

export default {
  name: 'LocationSelector',
  components: { AddressIcon },
  props: {
    showChip: { type: Boolean, default: true },
    showIcon: { type: Boolean, default: true },
    dark: { type: Boolean, default: true },
    chipColor: { type: String, default: 'white' },
  },
  data: () => ({
    showLocationDialog: false,
    locationSearch: '',
    selectedPlaceId: null,
    citySuggestions: [],
    cityLoading: false,
  }),
  computed: {
    ...mapState({
      gettingLocation: state => state.location.gettingLocation,
      locationError: state => state.location.locationError,
      currentCity: state => state.location.currentCity,
      currentProvince: state => state.location.currentProvince,
    }),
    chipTextColor() {
      return this.dark ? 'white' : 'primary'
    },
    textColorClass() {
      return this.dark ? 'white--text' : 'primary--text'
    }
  },
  methods: {
    ...mapActions('location', ['suggestCities', 'resolvePlace', 'getNewLocation']),

    accentInsensitiveFilter(item, queryText, itemText) {
      const normalize = s => (s || '')
        .toString()
        .normalize('NFD')
        .replace(/\p{Diacritic}+/gu, '')
        .toLowerCase()
      const q = normalize(queryText)
      const text = normalize(itemText)
      return text.indexOf(q) > -1
    },

    onCityInput(val) {
      debounce(function() {
        if (!val || val.length < 2) {
          this.citySuggestions = []
          return
        }
        this.cityLoading = true
        this.suggestCities(val)
          .then(list => { this.citySuggestions = list || [] })
          .finally(() => { this.cityLoading = false })
      }.bind(this), 300)()
    },

    async onCitySelected(placeId) {
      if (!placeId) return
      this.cityLoading = true
      try {
        const resolved = await this.resolvePlace(placeId)
        if (resolved && resolved.location) {
          const { city, province, location } = resolved
          localStorage.setItem('currentLocation', JSON.stringify({ lat: location.lat, lng: location.lng }))
          localStorage.setItem('currentCity', city)
          if (province) localStorage.setItem('currentProvince', province)
          localStorage.setItem('currentLocationTime', Date.now().toString())

          this.$store.commit('location/SET_CURRENT_LOCATION', { latitude: location.lat, longitude: location.lng })
          this.$store.commit('location/SET_CURRENT_CITY', city)
          this.$store.commit('location/SET_CURRENT_PROVINCE', province)

          this.$emit('changed', { city, province, location })
          this.$store.commit('location/SET_LOCATION_ERROR', null)
        }
      } catch (e) {
        console.error('onCitySelected error', e)
        this.$store.commit('location/SET_LOCATION_ERROR', 'No se pudo resolver la ciudad seleccionada')
      } finally {
        this.cityLoading = false
      }
    },

    async confirmLocation() {
      try {
        if (this.selectedPlaceId) {
          const resolved = await this.resolvePlace(this.selectedPlaceId)
          if (resolved && resolved.location) {
            const { city, province, location } = resolved
            localStorage.setItem('currentLocation', JSON.stringify({ lat: location.lat, lng: location.lng }))
            localStorage.setItem('currentCity', city)
            if (province) localStorage.setItem('currentProvince', province)
            localStorage.setItem('currentLocationTime', Date.now().toString())

            this.$store.commit('location/SET_CURRENT_LOCATION', { latitude: location.lat, longitude: location.lng })
            this.$store.commit('location/SET_CURRENT_CITY', city)
            this.$store.commit('location/SET_CURRENT_PROVINCE', province)

            this.$emit('changed', { city, province, location })
            this.$store.commit('location/SET_LOCATION_ERROR', null)
            this.showLocationDialog = false
            return
          }
        }

        if (this.locationSearch && this.locationSearch.length >= 2) {
          const list = await this.suggestCities(this.locationSearch)
          if (Array.isArray(list) && list.length > 0) {
            this.selectedPlaceId = list[0].place_id
            await this.onCitySelected(this.selectedPlaceId)
            this.showLocationDialog = false
            return
          }
        }

        this.$store.commit('location/SET_LOCATION_ERROR', 'Por favor seleccioná una ciudad del listado')
      } catch (error) {
        console.error('Error al confirmar ubicación:', error)
        this.$store.commit('location/SET_LOCATION_ERROR', 'Error al obtener la ubicación')
      }
    },

    async getCurrentLocation() {
      try {
        const locationData = await this.getNewLocation()
        if (locationData) {
          const { latitude, longitude } = locationData.location
          const city = this.$store.state.location.currentCity
          const province = this.$store.state.location.currentProvince
          this.$emit('changed', { city, province, location: { lat: latitude, lng: longitude } })
          this.showLocationDialog = false
        }
      } catch (error) {
        console.error('Error al obtener ubicación:', error)
        this.$store.dispatch('snackbar/show', {
          text: 'No se pudo obtener tu ubicación. Por favor, habilitá el acceso a la ubicación en tu navegador.',
          color: 'error'
        })
      }
    },

    closeLocationDialog() {
      this.showLocationDialog = false
      this.locationSearch = ''
      this.selectedPlaceId = null
      this.$store.commit('location/SET_LOCATION_ERROR', '')
    }
  }
}
</script>

<style scoped>
.location-chip {
  width: 100%;
  justify-content: flex-start;
  background: rgba(255, 255, 255, 0.15) !important;
}
.location-text {
  margin: 0 8px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}
</style>
